import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRupiah } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Clock, ChefHat, Volume2, VolumeX, LogOut, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

function playBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    [0, 0.2, 0.4].forEach((off) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.setValueAtTime(1200, now + off);
      gain.gain.setValueAtTime(0.0001, now + off);
      gain.gain.exponentialRampToValueAtTime(0.5, now + off + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + off + 0.14);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + off);
      osc.stop(now + off + 0.18);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {}
}

function elapsedLabel(iso) {
  const t = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return "baru saja";
  if (mins < 60) return `${mins} menit`;
  const h = Math.floor(mins / 60);
  return `${h} jam ${mins % 60} mnt`;
}

export default function KitchenPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("kasir_kitchen_sound") !== "0");
  const [fullscreen, setFullscreen] = useState(false);
  const [, setTick] = useState(0);
  const prevRef = useRef(null);
  const firstRef = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/orders?status=paid");
      const nowIds = new Set(data.map((o) => o.id));
      if (prevRef.current && !firstRef.current) {
        const newOnes = data.filter((o) => !prevRef.current.has(o.id));
        if (newOnes.length > 0) {
          if (soundOn) playBeep();
          if (navigator.vibrate) navigator.vibrate([180, 60, 180, 60, 180]);
        }
      }
      firstRef.current = false;
      prevRef.current = nowIds;
      setOrders(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    const t = setInterval(() => setTick((x) => x + 1), 15000);
    return () => { clearInterval(id); clearInterval(t); };
    // eslint-disable-next-line
  }, [soundOn]);

  const complete = async (o) => {
    await api.put(`/orders/${o.id}/status?status=completed`);
    toast.success(`Pesanan #${o.order_no} siap diantar`);
    load();
  };

  const toggleSound = () => {
    const n = !soundOn;
    setSoundOn(n);
    localStorage.setItem("kasir_kitchen_sound", n ? "1" : "0");
    if (n) playBeep();
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch {}
  };

  const doLogout = async () => {
    await logout();
    navigate("/owner/login");
  };

  return (
    <div className="min-h-screen bg-[#1E3A2B] text-white">
      <header className="sticky top-0 z-20 bg-[#0f261b] px-5 py-3 flex items-center justify-between border-b border-[#2b5240]">
        <div className="flex items-center gap-3">
          <ChefHat className="w-6 h-6 text-[#D4A373]" />
          <div>
            <div className="font-display text-xl font-semibold">Mode Dapur</div>
            <div className="text-xs text-[#9bb2a1]">{orders.length} antrean · auto-refresh 5 dtk</div>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button
            data-testid="btn-kitchen-sound"
            onClick={toggleSound}
            className={`px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2 ${soundOn ? "bg-[#D4A373] text-[#1E3A2B]" : "bg-[#2b5240] text-white"}`}
            title={soundOn ? "Suara aktif" : "Suara mati"}
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button data-testid="btn-kitchen-fullscreen" onClick={toggleFullscreen} className="px-3 py-2 rounded-xl text-sm bg-[#2b5240] text-white inline-flex items-center gap-2">
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button data-testid="btn-kitchen-exit" onClick={() => navigate("/owner/orders")} className="px-3 py-2 rounded-xl text-sm bg-[#2b5240] text-white">
            Kembali
          </button>
          <button data-testid="btn-kitchen-logout" onClick={doLogout} className="px-3 py-2 rounded-xl text-sm bg-[#D64045] text-white inline-flex items-center gap-2">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-5">
        {orders.length === 0 ? (
          <div className="text-center py-24">
            <ChefHat className="w-16 h-16 text-[#2b5240] mx-auto mb-4" />
            <div className="font-display text-2xl text-[#9bb2a1]">Tidak ada antrean</div>
            <div className="text-sm text-[#9bb2a1]/70 mt-2">Antrean dapur akan muncul di sini saat pelanggan sudah bayar.</div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="kitchen-grid">
            {orders.map((o) => {
              const mins = Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
              const urgent = mins >= 10;
              return (
                <div
                  key={o.id}
                  data-testid={`kitchen-order-${o.id}`}
                  className={`rounded-2xl p-4 border-2 ${urgent ? "bg-[#D64045] border-[#ff5b60]" : "bg-[#2b5240] border-[#3e6e58]"}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-3xl font-semibold">Meja {o.table_number}</div>
                      <div className="text-sm text-white/80">#{o.order_no}</div>
                    </div>
                    <div className={`text-right ${urgent ? "text-white" : "text-[#D4A373]"}`}>
                      <div className="flex items-center gap-1 justify-end">
                        <Clock className="w-4 h-4" />
                        <span className="font-display font-semibold" data-testid={`kitchen-elapsed-${o.id}`}>{elapsedLabel(o.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5 border-t border-white/20 pt-3">
                    {o.items.map((it, idx) => (
                      <div key={idx} className="text-base leading-snug">
                        <div className="flex items-start">
                          <span className="font-display font-bold text-xl mr-2 min-w-[2ch]">{it.quantity}×</span>
                          <div>
                            <div>{it.name}</div>
                            {it.options?.length > 0 && <div className="text-xs text-white/80">{it.options.map((op) => op.name).join(", ")}</div>}
                            {it.notes && <div className="text-xs text-[#D4A373] italic">📝 {it.notes}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {o.customer_name && <div className="mt-3 text-xs text-white/80">a.n. {o.customer_name}</div>}

                  <button
                    data-testid={`btn-kitchen-ready-${o.id}`}
                    onClick={() => complete(o)}
                    className="mt-4 w-full bg-white text-[#1E3A2B] py-3 rounded-xl font-display text-lg font-semibold hover:bg-[#F9F9F6] inline-flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" /> Siap Diantar
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
